"use strict";
var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b ||= {})
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { clamp } from "lodash";
import classNames from "classnames";
import { TextInput } from "../TextInput/TextInput";
import { Stack } from "../Stack/Stack";
import Tooltip, { TooltipSize } from "../Tooltip/Tooltip";
import { ChevronDown, ChevronUp, IconSize } from "../../icons";
import IconButton from "../IconButton/IconButton";
import { ButtonSize } from "../Button/Button";
import { useInterval } from "../../../utils";
import * as styles from "./NumberInput-css";
export const NumberInput = forwardRef(
  (_a, ref) => {
    var _b = _a, {
      value,
      onChange,
      onBlur,
      step = 1,
      snap,
      formatValue: customFormatValue,
      parseValue: customParseValue,
      min,
      max,
      hideStepper = false,
      variant = "bordered",
      size = "m",
      autoGrow,
      className,
      disabled,
      readOnly
    } = _b, props = __objRest(_b, [
      "value",
      "onChange",
      "onBlur",
      "step",
      "snap",
      "formatValue",
      "parseValue",
      "min",
      "max",
      "hideStepper",
      "variant",
      "size",
      "autoGrow",
      "className",
      "disabled",
      "readOnly"
    ]);
    const defaultFormatValue = useCallback((value2) => value2.toString(), []);
    const formatValue = customFormatValue || defaultFormatValue;
    const defaultParseValue = useCallback((value2) => {
      const numeric = value2 == null ? void 0 : value2.replace(/[^0-9.-]+/g, "");
      if (numeric === "")
        return NaN;
      return Number(numeric);
    }, []);
    const parseValue = customParseValue || defaultParseValue;
    const [internalValue, setInternalValue] = useState(formatValue(value));
    const [showBoundaryError, setShowBoundaryError] = useState(false);
    const [boundaryError, setBoundaryError] = useState("");
    const [pressedDirection, setPressedDirection] = useState(null);
    const boundaryErrorTimer = useRef();
    const pressedStartTimer = useRef();
    const containerRef = useRef(null);
    const isAtMax = max !== void 0 && value >= max;
    const isAtMin = min !== void 0 && value <= min;
    const stepperVisible = !readOnly && !hideStepper;
    const clearPressTimer = () => {
      if (pressedStartTimer.current) {
        clearTimeout(pressedStartTimer.current);
      }
    };
    useInterval(
      () => {
        if (!pressedDirection)
          return;
        stepValue(pressedDirection);
      },
      pressedDirection ? 20 : null
    );
    useEffect(() => {
      setInternalValue(formatValue(value));
    }, [value, formatValue]);
    useEffect(() => {
      return () => clearPressTimer();
    }, []);
    useEffect(() => {
      const internalValueNumber = internalValueToNumber(internalValue);
      if (formatValue(constrainValue(internalValueNumber)) === internalValue && internalValueNumber !== value) {
        commitChange();
      }
    }, [internalValue]);
    const internalValueToNumber = (stringValue) => {
      const numericValue = parseValue(stringValue);
      if (isNaN(numericValue)) {
        return value;
      }
      return numericValue;
    };
    const handleBoundaryErrors = (min2, max2, value2) => {
      const minExists = min2 !== void 0;
      const maxExists = max2 !== void 0;
      clearTimeout(boundaryErrorTimer.current);
      const showError = () => {
        setShowBoundaryError(true);
        boundaryErrorTimer.current = setTimeout(() => {
          setShowBoundaryError(false);
        }, 3e3);
      };
      if (minExists && maxExists && (value2 < min2 || value2 > max2)) {
        setBoundaryError(`Must be between ${min2} and ${max2}`);
        showError();
      } else if (minExists && value2 < min2) {
        setBoundaryError(`Must be at least ${min2}`);
        showError();
      } else if (maxExists && value2 > max2) {
        setBoundaryError(`Must be at most ${max2}`);
        showError();
      } else {
        setShowBoundaryError(false);
      }
    };
    const constrainValue = (value2) => {
      const snapped = step && snap ? safeMultiply(Math.round(value2 / step), step) : value2;
      const constrainedValue = clamp(
        step ? snapped : value2,
        min !== void 0 ? min : -Infinity,
        max !== void 0 ? max : Infinity
      );
      handleBoundaryErrors(min, max, value2);
      return constrainedValue;
    };
    const handleInternalChange = (value2) => {
      setInternalValue(value2);
    };
    const commitChange = () => {
      const numberValue = constrainValue(internalValueToNumber(internalValue));
      onChange(numberValue);
    };
    const formatInternalValue = () => {
      setInternalValue(formatValue(constrainValue(internalValueToNumber(internalValue))));
    };
    const handleBlur = (event) => {
      onBlur == null ? void 0 : onBlur(event);
      formatInternalValue();
    };
    const stepValue = (direction) => {
      const numberValue = internalValueToNumber(internalValue);
      const finalValue = constrainValue(safePlus(numberValue, step * direction));
      const formattedValue = formatValue(finalValue);
      setInternalValue(formattedValue);
    };
    const handleKeyDown = (event) => {
      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          stepValue(1);
          break;
        case "ArrowDown":
          event.preventDefault();
          stepValue(-1);
          break;
        case "Enter":
          formatInternalValue();
          break;
      }
    };
    const handleMinClick = () => {
      if (isAtMin) {
        handleBoundaryErrors(min, max, value - step);
      }
    };
    const handleMaxClick = () => {
      if (isAtMax) {
        handleBoundaryErrors(min, max, value + step);
      }
    };
    const handlePress = (direction) => {
      if (readOnly)
        return;
      clearPressTimer();
      const clearPressedDirection = () => {
        setPressedDirection(null);
        clearPressTimer();
        document.removeEventListener("mouseup", clearPressedDirection);
      };
      stepValue(direction);
      pressedStartTimer.current = setTimeout(() => setPressedDirection(direction), 600);
      document.addEventListener("mouseup", clearPressedDirection);
    };
    return /* @__PURE__ */ React.createElement(
      Stack,
      {
        className: classNames(className, styles.container({ variant, stepperVisible, size, autoGrow, disabled })),
        direction: "horizontal",
        onKeyDown: handleKeyDown,
        ref: containerRef
      },
      /* @__PURE__ */ React.createElement(
        Tooltip,
        {
          isError: true,
          targetClassName: styles.tooltipWrapper({ autoGrow }),
          show: showBoundaryError,
          size: TooltipSize.Large,
          content: boundaryError
        },
        /* @__PURE__ */ React.createElement(
          TextInput,
          __spreadValues({
            value: internalValue,
            onChange: handleInternalChange,
            onBlur: handleBlur,
            variant,
            size,
            autoGrow,
            inputMode: "numeric",
            role: "spinbutton",
            "aria-valuenow": value,
            "aria-valuetext": internalValue,
            "aria-valuemin": min,
            "aria-valuemax": max,
            ref,
            disabled,
            readOnly
          }, props)
        )
      ),
      stepperVisible && /* @__PURE__ */ React.createElement(Stack, { relative: true, className: styles.buttons({ variant, size }), align: "center", justify: "spaceBetween" }, /* @__PURE__ */ React.createElement("div", { onMouseDown: handleMaxClick }, /* @__PURE__ */ React.createElement(
        IconButton,
        {
          className: styles.button,
          size: ButtonSize.XSmall,
          icon: /* @__PURE__ */ React.createElement(ChevronUp, { size: IconSize.Small }),
          onMouseDown: () => handlePress(1),
          "aria-label": "Increase",
          disabled: disabled || isAtMax,
          tabIndex: -1
        }
      )), /* @__PURE__ */ React.createElement("div", { onMouseDown: handleMinClick }, /* @__PURE__ */ React.createElement(
        IconButton,
        {
          className: styles.button,
          size: ButtonSize.XSmall,
          icon: /* @__PURE__ */ React.createElement(ChevronDown, { size: IconSize.Small }),
          onMouseDown: () => handlePress(-1),
          "aria-label": "Decrease",
          disabled: disabled || isAtMin,
          tabIndex: -1
        }
      )))
    );
  }
);
function getPrecision(a) {
  var _a;
  return ((_a = `${a}`.split(".").at(-1)) == null ? void 0 : _a.length) || 0;
}
function safePlus(a, b) {
  const precisionA = getPrecision(a);
  const precisionB = getPrecision(b);
  if (precisionA === 0 || precisionB === 0)
    return a + b;
  return Number((a + b).toFixed(Math.max(precisionA, precisionB)));
}
function safeMultiply(a, b) {
  const precisionA = getPrecision(a);
  const precisionB = getPrecision(b);
  if (precisionA === 0 || precisionB === 0)
    return a * b;
  return Number((a * b).toFixed(Math.max(precisionA, precisionB)));
}
